import { Injectable, Optional } from '@angular/core';
import { Menus } from '../../app/app.menu';
import { CurrentUser, Role } from '@otas/model/current-user';
import * as _ from 'underscore';
import { ModuleVersionService } from '../../app/services/module-version.service';
import {
  VAZHIPADU_MODULE, applyVazhipaduVersionToMenus,
  applyVenueModuleToMenus, DEFAULT_VENUE_MODULE
} from '../../app/services/module-version-menu';
import { VenueModuleService } from '../../app/services/venue-module.service';
export interface MenuItem {
  id: string;
  text: string;
  icon: string;
  route: string;
  active: boolean;
  roles: Array<string>;
  submenu: Array<MenuItem>;
  megamenu?: boolean;
  groupBy?: string;
  mmgroupBy?: string;
  sortOrder?: number;
}
@Injectable()
export class MenuService {
  items: Array<MenuItem>;

  /**
   * `venueModuleService` is @Optional AND defaulted so the historic
   * `new MenuService(null)` call shape (menu.service.spec) keeps compiling.
   */
  constructor(@Optional() private moduleVersionService: ModuleVersionService | null,
    @Optional() private venueModuleService: VenueModuleService | null = null) {
    // this.setMenuITEMS();
  }

  setMenuITEMS() {
    const currentUser = !!sessionStorage.getItem('CURRENT_USER') ? JSON.parse(sessionStorage.getItem('CURRENT_USER')) : null;
    let menus = new Menus().menus;
    // Workstream E phase 2: the per-temple VAZHIPADU module toggle rewires
    // the Vazhipadu entries/defaults. Each version shows ONLY its own
    // screens (V2 strips the "(Classic)" entries; V1 rewires the primaries
    // to the classic routes and strips the V2-only report entries) - the
    // Settings > Module Versions toggle is the switch between the sets.
    const vazhipaduVersion = this.moduleVersionService
      ? this.moduleVersionService.version(VAZHIPADU_MODULE)() : 'V2';
    menus = applyVazhipaduVersionToMenus(menus, vazhipaduVersion);
    // WS-FHP (owner decision D-FHP-9): the venue module is strictly either/or
    // - a temple runs Auditorium OR Function Hall, never both. Whichever is
    // not in force has its booking/enquiry entries stripped here; both route
    // sets stay registered (URL-level rollback, same as the V1/V2 toggle).
    // Default AUDITORIUM, so an existing temple's menu is unchanged until it
    // switches on the Function Hall Settings screen.
    const venueModule = this.venueModuleService
      ? this.venueModuleService.current() : DEFAULT_VENUE_MODULE;
    menus = applyVenueModuleToMenus(menus, venueModule);
    if (!currentUser.isOnlineSync) {
      menus.forEach(element => {
        if (!!element.submenu) {
          element.submenu.forEach(el => {
            if (el.id == 'pooja-collection-report-online') {
              element.submenu.splice(element.submenu.indexOf(el), 1);
            }
          });
        }
      });
    }
    if (!currentUser.isStockInventory) {
      // R49 (owner directive 2026-07-25): the stock reports live in the
      // Reports megamenu's own 'Stock' column now (the top-level Stock head
      // is retired), so the no-stock-inventory gate strips that COLUMN
      // instead of the old head.
      menus.forEach(element => {
        if (!!element.submenu) {
          element.submenu = element.submenu.filter(el => el.mmgroupBy !== 'Stock');
        }
      });
      menus.forEach(element => {
        if (!!element.submenu) {
          element.submenu.forEach(el => {
            if (el.id == 'store-pass') {
              element.submenu.splice(element.submenu.indexOf(el), 1);
            }

          });
        }
      });
      menus.forEach(element => {
        if (!!element.submenu) {
          element.submenu.forEach(el => {
            if (el.id == 'store-transfer') {
              element.submenu.splice(element.submenu.indexOf(el), 1);
            }
          });
        }
      });
    }


    if (!!currentUser) {
      // Declaration order is the contract (owner directive 2026-07-23):
      // top-level menus render in app.menu.ts order (home, Administration,
      // Transaction, Enquiry, Reports, Help) and every submenu keeps its
      // declared order. The old per-role _.union appended menus in the order
      // the USER'S ROLES matched them, scrambling both levels for
      // multi-role users.
      const roleNames = new Set<string>(_.map(currentUser.roles, (role: Role) => role.name));
      const allowed = (item: MenuItem) => _.some(item.roles, (r: string) => roleNames.has(r));
      this.items = _.filter(menus, allowed);
      _.each(this.items, (headMenu: MenuItem) => {
        const submenu = _.filter(headMenu.submenu || [], (subMenu: MenuItem) => {
          subMenu.groupBy = headMenu.id;
          return allowed(subMenu);
        });
        // No matching children -> no panel at all (an empty array would
        // render an empty dropdown card on hover).
        headMenu.submenu = submenu.length ? submenu : null;
        // A menu goes megamenu when its items carry column groups (Reports)
        // OR its flat list can't fit ONE dropdown column on THIS viewport -
        // overflow then flows into extra columns (chunked by the mega-menu
        // component with the same rowsPerColumn figure). Decided per menu,
        // re-decided on window resize (nav-menu rebuilds).
        headMenu.megamenu = !!headMenu.submenu
          && (_.some(submenu, (s: MenuItem) => !!s.mmgroupBy)
            || submenu.length > MenuService.rowsPerColumn());
      });

    } else {
      this.items = [];

      const temp_menu = _.filter(menus, (menu: MenuItem) => {
        return _.contains(menu.roles, 'GUEST');
      });

      this.items = _.union(this.items, temp_menu);
    }
  }

  /**
   * How many flat submenu rows fit ONE dropdown column on this viewport:
   * panel top ~55px under the window top, ~16px panel padding, one row =
   * 8px padding x2 + ~21px line ≈ 37px. Shared by the megamenu decision
   * above AND the mega-menu component's flat-list chunking so the flip
   * point and the column height always agree. Floor of 6 keeps tiny
   * windows from degenerating into one-item columns.
   */
  static rowsPerColumn(): number {
    const viewport = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 900;
    return Math.max(6, Math.floor((viewport - 90) / 37));
  }
}
